import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Check, Copy, Plus, Save, Sparkles, Trash2 } from 'lucide-react'
import {
  clients, folders as foldersApi, preseedMatters,
  vpMapGet, vpMapIngest,
  type ClientSettings, type Folder, type PreseedMatter, type VpMapSummary,
} from '../../api'

/**
 * AI configuration — Studio surface.
 *
 * Why a Studio page instead of /settings: superusers (and anyone with
 * Studio access) need to inspect and edit per-client AI knobs across
 * tenants. The /settings page is the user's OWN-TENANT view; this is
 * the cross-tenant operator view.
 *
 * Edits:  `ai_auto_folder_enabled` (toggle on/off auto-foldering)
 * Read-only:  Client.context — the firm-level JSON blob exposed as
 *             {{client_context}} to every prompt. Surfaced here so
 *             operators can see exactly what context every prompt
 *             receives without poking the DB.
 *
 * `ai_domain_context` is hidden — its only consumer was the legacy
 * standalone auto_folder task. The in-DAG file_matter_into_folders
 * step does its own foldering using a dedicated prompt + the
 * folder_tree, current_filings, and global_filing_rules built-ins.
 * Field kept in the model for now in case we re-introduce it.
 */
const SELECTED_CLIENT_KEY = 'ai_config_selected_client_code'

export default function AIConfig() {
  const list = useQuery({ queryKey: ['clients'], queryFn: () => clients.list() })
  const allClients = list.data ?? []
  const [selectedId, setSelectedId] = useState<number | null>(null)

  // Resolve the initial selection in this priority order:
  //   1. Last client the user chose here, persisted by code (stable
  //      across deploys + tenant id renumbering)
  //   2. MOVU, the primary live tenant — sensible default while we have
  //      a single real client and a handful of demo/test ones
  //   3. First client in the list (alphabetical-ish from the API)
  // Runs once the list loads; subsequent picks come from the dropdown.
  useEffect(() => {
    if (selectedId != null || allClients.length === 0) return
    const savedCode = localStorage.getItem(SELECTED_CLIENT_KEY)
    const fromSaved = savedCode ? allClients.find((c) => c.code === savedCode) : null
    const fromDefault = allClients.find((c) => c.code === 'MOVU')
    const fallback = allClients[0]
    setSelectedId((fromSaved ?? fromDefault ?? fallback).id)
  }, [allClients, selectedId])

  const selected = useMemo(
    () => allClients.find((c) => c.id === selectedId) ?? null,
    [allClients, selectedId],
  )

  // Persist on every change so a new tab / refresh lands on the same
  // tenant. Stored as `code` rather than id — id can shift between
  // databases, code is stable.
  const onPick = (id: number) => {
    setSelectedId(id)
    const c = allClients.find((c) => c.id === id)
    if (c) localStorage.setItem(SELECTED_CLIENT_KEY, c.code)
  }

  return (
    <div className="me-scroll h-full overflow-auto">
      <div className="mx-auto max-w-3xl p-6">
      <div className="flex items-center gap-2">
        <Sparkles size={18} className="me-text-mute" />
        <h1 className="text-[18px] font-semibold me-text">AI configuration</h1>
      </div>
      <div className="mt-1 text-[12px] me-text-mute">
        Per-client AI knobs and a read-only view of the firm context
        that every prompt receives. Visible to operators with Studio access.
      </div>

      <div className="mt-6 me-card p-5">
        <div className="me-section-h">Client</div>
        <div className="text-[12px] me-text-mute mb-3">
          Pick which tenant's AI auto-folder config to view or edit.
        </div>
        <select
          value={selectedId ?? ''}
          onChange={(e) => onPick(Number(e.target.value))}
          className="me-input"
          disabled={list.isLoading}
        >
          {allClients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.code} — {c.name}
            </option>
          ))}
        </select>
      </div>

      {selected && <ClientAIConfigForm client={selected} />}

      {list.isLoading && (
        <div className="mt-6 me-text-mute text-[12px]">Loading clients…</div>
      )}
      </div>
    </div>
  )
}

function ClientAIConfigForm({ client }: { client: ClientSettings }) {
  const qc = useQueryClient()
  const [enabled, setEnabled] = useState<boolean>(client.ai_auto_folder_enabled ?? false)

  // Reset state when the client selection changes.
  useEffect(() => {
    setEnabled(client.ai_auto_folder_enabled ?? false)
  }, [client.id, client.ai_auto_folder_enabled])

  const save = useMutation({
    mutationFn: () => clients.update(client.id, {
      ai_auto_folder_enabled: enabled,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  })

  const isDirty = enabled !== (client.ai_auto_folder_enabled ?? false)

  return (
    <>
      <div className="mt-6 me-card p-5">
        <div className="me-section-h">Enable / disable</div>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="mt-0.5 cursor-pointer"
          />
          <div>
            <div className="font-medium me-text">
              Run AI auto-folder for <span className="me-mono">{client.code}</span>
            </div>
            <div className="text-[11.5px] me-text-mute leading-snug">
              When enabled, every matter touched by artifact processing gets
              evaluated by the AI for folder placement (subject to per-matter
              idempotency — unchanged matters are skipped). User-assigned
              folders are never overridden.
              {!enabled && (
                <span className="block mt-1 text-[color:var(--me-text)]">
                  Currently OFF — no AI filing for this tenant.
                </span>
              )}
            </div>
          </div>
        </label>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={() => save.mutate()}
          disabled={!isDirty || save.isPending}
          className={isDirty ? 'me-btn me-btn-primary' : 'me-btn'}
        >
          <Save size={12} />
          {save.isPending ? 'Saving…' : 'Save changes'}
        </button>
        {isDirty && (
          <button
            onClick={() => setEnabled(client.ai_auto_folder_enabled ?? false)}
            className="me-btn"
            disabled={save.isPending}
          >
            Discard
          </button>
        )}
        {save.isSuccess && !isDirty && (
          <span className="me-mono text-[11px] text-[color:var(--me-fresh)]">saved</span>
        )}
        {save.error && (
          <span className="text-[12px] text-[color:var(--me-urgent)]">
            {(save.error as Error).message}
          </span>
        )}
      </div>

      <ClientNamePanel client={client} />
      <ContractVpMapPanel client={client} />
      <ClientContextPanel client={client} />
      <FirmLexiconPanel client={client} />
      <GenericTermsPanel client={client} />
      <PreseedMattersPanel />
    </>
  )
}

// ─── Editable client_context panel ────────────────────────────────────────
//
// Structured editor for the four commonly-used keys of Client.context
// (firm_name / description / colleagues / notes) plus an "Other fields"
// key-value escape hatch for anything else. Shows a live JSON preview
// of what will be sent to prompts as {{client_context}}. Saving POSTs
// the rebuilt JSON to /clients/{id}/.

type Colleague = { name: string; email: string; role: string; notes: string }
type ExtraField = { key: string; value: string }

const KNOWN_KEYS = new Set(['firm_name', 'description', 'colleagues', 'notes'])

// Pull the known keys out of Client.context into structured state; whatever's
// left becomes an "extras" list of key/value pairs the user can edit.
function splitContext(ctx: Record<string, unknown>): {
  firmName: string
  description: string
  colleagues: Colleague[]
  notes: string
  extras: ExtraField[]
} {
  const colleaguesIn = Array.isArray(ctx.colleagues) ? ctx.colleagues : []
  const colleagues: Colleague[] = colleaguesIn.map((c) => {
    const o = (typeof c === 'object' && c) ? (c as Record<string, unknown>) : {}
    return {
      name: typeof o.name === 'string' ? o.name : '',
      email: typeof o.email === 'string' ? o.email : '',
      role: typeof o.role === 'string' ? o.role : '',
      notes: typeof o.notes === 'string' ? o.notes : '',
    }
  })
  const extras: ExtraField[] = []
  for (const [k, v] of Object.entries(ctx)) {
    if (KNOWN_KEYS.has(k)) continue
    // Non-string extras get JSON-stringified into the textbox; the user
    // can edit it as text and we'll try to parse on save (falling back
    // to the raw string if it doesn't parse).
    const sv = typeof v === 'string' ? v : JSON.stringify(v)
    extras.push({ key: k, value: sv })
  }
  return {
    firmName: typeof ctx.firm_name === 'string' ? ctx.firm_name : '',
    description: typeof ctx.description === 'string' ? ctx.description : '',
    colleagues,
    notes: typeof ctx.notes === 'string' ? ctx.notes : '',
    extras,
  }
}

// Rebuild the JSON object from structured state. Empty known keys are
// included as '' (so the model still sees the key, just blank) UNLESS
// every structured field is empty, in which case we omit it entirely
// to keep the stored JSON tidy. Extras whose key is blank are dropped.
function buildContext(s: {
  firmName: string; description: string; colleagues: Colleague[];
  notes: string; extras: ExtraField[]
}): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (s.firmName.trim()) out.firm_name = s.firmName
  if (s.description.trim()) out.description = s.description
  // Drop entirely-empty colleague rows so we don't bloat the JSON with
  // {name:'',email:'',role:'',notes:''} placeholders the user added
  // and then never filled in.
  const cleanColleagues = s.colleagues.filter(
    (c) => c.name.trim() || c.email.trim() || c.role.trim() || c.notes.trim()
  )
  if (cleanColleagues.length > 0) out.colleagues = cleanColleagues
  if (s.notes.trim()) out.notes = s.notes
  for (const e of s.extras) {
    const k = e.key.trim()
    if (!k) continue
    // Try to parse as JSON so the user can put objects/arrays/numbers in.
    // If parsing fails, fall back to the raw string — which is the
    // common case for plain text values.
    let parsed: unknown = e.value
    try { parsed = JSON.parse(e.value) } catch { /* keep string */ }
    out[k] = parsed
  }
  return out
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Recursively sort object keys so two semantically-equal JSON values
// compare equal as strings. Without this, MOVU's saved context (keys in
// insertion order `notes, firm_name, colleagues, description`) doesn't
// match what buildContext emits (`firm_name, description, colleagues,
// notes`) and the dirty check fires immediately on load.
function canonicalize(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(canonicalize)
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {}
    for (const k of Object.keys(v as object).sort()) {
      out[k] = canonicalize((v as Record<string, unknown>)[k])
    }
    return out
  }
  return v
}

// ─── Editable client_name panel ───────────────────────────────────────────
//
// A small identity variable exposed to prompts as {{client_name}} — the
// client's name plus common aliases. Used by steps that only need to know who
// the client (our side) is, e.g. the CIC agreement read telling the client
// from the counterparty, without shipping the whole {{client_context}} blob.
// Blank falls back to the tenant name server-side.
function ClientNamePanel({ client }: { client: ClientSettings }) {
  const qc = useQueryClient()
  const [val, setVal] = useState<string>(client.client_name ?? '')

  useEffect(() => { setVal(client.client_name ?? '') }, [client.id, client.client_name])

  const save = useMutation({
    mutationFn: () => clients.update(client.id, { client_name: val } as Partial<ClientSettings>),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  })
  const isDirty = val !== (client.client_name ?? '')

  return (
    <div className="mt-6 me-card p-5">
      <div className="me-section-h">Client name</div>
      <div className="text-[12px] me-text-mute mb-3 leading-snug">
        The small identity variable injected into prompts as{' '}
        <span className="me-mono">{'{{client_name}}'}</span> — the client's name
        and common aliases, so a step can tell the client (our side) from the
        counterparty without the full{' '}
        <span className="me-mono">{'{{client_context}}'}</span> blob. One per
        line or semicolon-separated. Blank falls back to{' '}
        <span className="me-mono">{client.name}</span>.
      </div>
      <textarea
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder={`e.g.\n${client.name}\nthe University\n${client.code}`}
        rows={3}
        className="me-input"
        style={{ fontFamily: 'inherit', lineHeight: 1.5 }}
      />
      <div className="mt-3 flex items-center gap-3">
        <button
          onClick={() => save.mutate()}
          disabled={!isDirty || save.isPending}
          className={isDirty ? 'me-btn me-btn-primary' : 'me-btn'}
        >
          <Save size={12} />
          {save.isPending ? 'Saving…' : 'Save client name'}
        </button>
        {isDirty && (
          <button onClick={() => setVal(client.client_name ?? '')} className="me-btn" disabled={save.isPending}>
            Discard
          </button>
        )}
        {save.isSuccess && !isDirty && (
          <span className="me-mono text-[11px] text-[color:var(--me-fresh)]">saved</span>
        )}
        {save.error && (
          <span className="text-[12px] text-[color:var(--me-urgent)]">
            {(save.error as Error).message}
          </span>
        )}
      </div>
    </div>
  )
}

// ─── Contract VP map panel ────────────────────────────────────────────────
// Submitter → approving VP, from the Maria/Heather-approved sheet. Paste the CSV;
// the server parses it (headers by name, extras ignored, "Submitter is a VP" =
// self, blank VP = neutral placeholder) into Client.contract_vp_map — a dedicated
// field read only by the CIC VP step, NOT shipped in {{client_context}}.
function ContractVpMapPanel({ client }: { client: ClientSettings }) {
  const [csv, setCsv] = useState('')
  const [summary, setSummary] = useState<VpMapSummary | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    let alive = true
    vpMapGet(client.code).then((s) => { if (alive) setSummary(s) }).catch(() => {})
    return () => { alive = false }
  }, [client.code])

  async function save() {
    setBusy(true); setErr('')
    try {
      const r = await vpMapIngest(client.code, csv)
      setSummary(r.summary)
      setCsv('')
    } catch (e) {
      const ax = e as { response?: { data?: { detail?: string } } }
      setErr(ax?.response?.data?.detail || (e as Error).message)
    } finally { setBusy(false) }
  }

  return (
    <div className="mt-6 me-card p-5">
      <div className="me-section-h">Contract VP map</div>
      <div className="text-[12px] me-text-mute mb-3 leading-snug">
        Submitter → approving VP, from the approved sheet. Columns:{' '}
        <span className="me-mono">Email · Submitter (First Last) · Submitter's VP (First Last)</span>{' '}
        — any other columns are ignored. "<span className="me-mono">Submitter is a VP</span>" marks a
        self-approver; a blank VP cell becomes a neutral placeholder. Dedicated to the CIC VP step —
        not shipped in <span className="me-mono">{'{{client_context}}'}</span>.
      </div>
      {summary && (
        <div className="text-[11.5px] me-text mb-2">
          Loaded: <b>{summary.count}</b> entries · {summary.self} self · {summary.placeholder} placeholder ·{' '}
          <span className={summary.unresolved.length ? 'text-[color:var(--me-urgent)]' : ''}>
            {summary.unresolved.length} unresolved
          </span>
          {summary.unresolved.length > 0 && (
            <details className="mt-1">
              <summary className="cursor-pointer me-text-mute">unresolved VP names (no matching submitter row)</summary>
              <pre className="me-mono text-[10.5px] whitespace-pre-wrap">
                {summary.unresolved.map((u) => `${u.submitter}  →  ${u.vp_name}`).join('\n')}
              </pre>
            </details>
          )}
        </div>
      )}
      <textarea
        value={csv}
        onChange={(e) => setCsv(e.target.value)}
        rows={6}
        placeholder="Paste the approved CSV here, including its header row…"
        className="me-input me-mono !text-[11.5px]"
      />
      <div className="mt-3 flex items-center gap-3">
        <button onClick={save} disabled={busy || !csv.trim()}
                className={csv.trim() ? 'me-btn me-btn-primary' : 'me-btn'}>
          <Save size={12} /> {busy ? 'Importing…' : 'Import CSV'}
        </button>
        {err && <span className="text-[12px] text-[color:var(--me-urgent)]">{err}</span>}
      </div>
    </div>
  )
}

function ClientContextPanel({ client }: { client: ClientSettings }) {
  const qc = useQueryClient()
  const initial = useMemo(
    () => splitContext((client.context ?? {}) as Record<string, unknown>),
    [client.context]
  )
  const [firmName, setFirmName] = useState(initial.firmName)
  const [description, setDescription] = useState(initial.description)
  const [colleagues, setColleagues] = useState<Colleague[]>(initial.colleagues)
  const [notes, setNotes] = useState(initial.notes)
  const [extras, setExtras] = useState<ExtraField[]>(initial.extras)

  // Reset when the client selection changes.
  useEffect(() => {
    setFirmName(initial.firmName)
    setDescription(initial.description)
    setColleagues(initial.colleagues)
    setNotes(initial.notes)
    setExtras(initial.extras)
  }, [client.id, initial])

  const built = useMemo(
    () => buildContext({ firmName, description, colleagues, notes, extras }),
    [firmName, description, colleagues, notes, extras]
  )
  const preview = JSON.stringify(built, null, 2)

  // Light realtime validation. Emails must look like emails (or be
  // blank — partly-filled rows shouldn't block save). Extra-field keys
  // must be unique and not collide with the structured keys.
  const errors: string[] = []
  for (let i = 0; i < colleagues.length; i++) {
    const c = colleagues[i]
    if (c.email && !EMAIL_RE.test(c.email)) {
      errors.push(`Colleague #${i + 1}: email "${c.email}" looks invalid`)
    }
  }
  const extraKeys = extras.map((e) => e.key.trim()).filter(Boolean)
  const dupes = extraKeys.filter((k, i) => extraKeys.indexOf(k) !== i)
  if (dupes.length > 0) {
    errors.push(`Duplicate extra-field keys: ${[...new Set(dupes)].join(', ')}`)
  }
  const collisions = extraKeys.filter((k) => KNOWN_KEYS.has(k))
  if (collisions.length > 0) {
    errors.push(`Extra-field keys collide with reserved keys: ${collisions.join(', ')}`)
  }

  const save = useMutation({
    mutationFn: () => clients.update(client.id, { context: built }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  })

  const isDirty =
    JSON.stringify(canonicalize(built)) !==
    JSON.stringify(canonicalize(client.context ?? {}))

  const updateColleague = (i: number, patch: Partial<Colleague>) => {
    setColleagues((prev) => prev.map((c, j) => j === i ? { ...c, ...patch } : c))
  }
  const removeColleague = (i: number) => {
    setColleagues((prev) => prev.filter((_, j) => j !== i))
  }
  const addColleague = () => {
    setColleagues((prev) => [...prev, { name: '', email: '', role: '', notes: '' }])
  }
  const updateExtra = (i: number, patch: Partial<ExtraField>) => {
    setExtras((prev) => prev.map((e, j) => j === i ? { ...e, ...patch } : e))
  }
  const removeExtra = (i: number) => {
    setExtras((prev) => prev.filter((_, j) => j !== i))
  }
  const addExtra = () => setExtras((prev) => [...prev, { key: '', value: '' }])

  return (
    <div className="mt-6 me-card p-5">
      <div className="me-section-h">Client context</div>
      <div className="text-[12px] me-text-mute mb-4 leading-snug">
        The firm-level JSON injected into every prompt step as{' '}
        <span className="me-mono">{'{{client_context}}'}</span>. Fields below
        are common keys; the model sees all of them plus anything in "Other
        fields". The runner also extracts <span className="me-mono">{'{{firm_name}}'}</span>{' '}
        and <span className="me-mono">{'{{colleagues}}'}</span> as separate variables.
      </div>

      {/* Common keys ─────────────────────────────────── */}
      <div className="space-y-3">
        <div>
          <label className="block text-[11.5px] font-semibold me-text mb-1">
            firm_name
          </label>
          <input
            type="text"
            value={firmName}
            onChange={(e) => setFirmName(e.target.value)}
            placeholder={`(default: "${client.name}")`}
            className="me-input"
          />
        </div>
        <div>
          <label className="block text-[11.5px] font-semibold me-text mb-1">
            description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="1-3 sentences about what the firm does."
            rows={3}
            className="me-input"
            style={{ fontFamily: 'inherit', lineHeight: 1.5 }}
          />
        </div>
        <div>
          <label className="block text-[11.5px] font-semibold me-text mb-1">
            notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything else worth flagging to the model."
            rows={3}
            className="me-input"
            style={{ fontFamily: 'inherit', lineHeight: 1.5 }}
          />
        </div>
      </div>

      {/* Colleagues ─────────────────────────────────── */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <label className="text-[11.5px] font-semibold me-text">
            colleagues
            <span className="ml-1 me-text-mute font-normal">
              ({colleagues.length} {colleagues.length === 1 ? 'entry' : 'entries'})
            </span>
          </label>
          <button onClick={addColleague} className="me-btn me-btn-ghost text-[11px]">
            <Plus size={10} /> Add colleague
          </button>
        </div>
        {colleagues.length === 0 && (
          <div className="text-[11.5px] me-text-mute italic">
            No colleagues — click "Add colleague" to define one.
          </div>
        )}
        <div className="space-y-2">
          {colleagues.map((c, i) => {
            const emailInvalid = c.email && !EMAIL_RE.test(c.email)
            return (
              <div
                key={i}
                className="grid grid-cols-[1fr_1.4fr_1fr_1.6fr_auto] gap-2 items-start"
              >
                <input
                  type="text"
                  value={c.name}
                  onChange={(e) => updateColleague(i, { name: e.target.value })}
                  placeholder="Name"
                  className="me-input text-[12px]"
                />
                <input
                  type="email"
                  value={c.email}
                  onChange={(e) => updateColleague(i, { email: e.target.value })}
                  placeholder="email@firm.com"
                  className="me-input text-[12px]"
                  style={emailInvalid ? { borderColor: 'var(--me-urgent)' } : undefined}
                />
                <input
                  type="text"
                  value={c.role}
                  onChange={(e) => updateColleague(i, { role: e.target.value })}
                  placeholder="Role"
                  className="me-input text-[12px]"
                />
                <input
                  type="text"
                  value={c.notes}
                  onChange={(e) => updateColleague(i, { notes: e.target.value })}
                  placeholder="Notes"
                  className="me-input text-[12px]"
                />
                <button
                  onClick={() => removeColleague(i)}
                  className="me-btn me-btn-ghost"
                  title="Remove this colleague"
                  aria-label="Remove colleague"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            )
          })}
        </div>
      </div>

      {/* Extra free-form fields ─────────────────────────── */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <label className="text-[11.5px] font-semibold me-text">
            Other fields
            <span className="ml-2 text-[10.5px] me-text-mute font-normal">
              free-form keys — values try to JSON-parse; otherwise stored as a string
            </span>
          </label>
          <button onClick={addExtra} className="me-btn me-btn-ghost text-[11px]">
            <Plus size={10} /> Add field
          </button>
        </div>
        {extras.length === 0 && (
          <div className="text-[11.5px] me-text-mute italic">
            No extras. Use these for anything outside the structured fields above
            (e.g. <span className="me-mono">practice_areas</span>,{' '}
            <span className="me-mono">style_preferences</span>).
          </div>
        )}
        <div className="space-y-2">
          {extras.map((e, i) => (
            <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-start">
              <input
                type="text"
                value={e.key}
                onChange={(ev) => updateExtra(i, { key: ev.target.value })}
                placeholder="key"
                className="me-input me-mono text-[12px]"
              />
              <input
                type="text"
                value={e.value}
                onChange={(ev) => updateExtra(i, { value: ev.target.value })}
                placeholder='value (string, or JSON like ["a","b"])'
                className="me-input text-[12px]"
              />
              <button
                onClick={() => removeExtra(i)}
                className="me-btn me-btn-ghost"
                title="Remove this field"
                aria-label="Remove field"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Validation + save ──────────────────────────────── */}
      {errors.length > 0 && (
        <div className="mt-4 text-[11.5px] text-[color:var(--me-urgent)] space-y-0.5">
          {errors.map((m, i) => <div key={i}>⚠ {m}</div>)}
        </div>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={() => save.mutate()}
          disabled={!isDirty || save.isPending || errors.length > 0}
          className={isDirty && errors.length === 0 ? 'me-btn me-btn-primary' : 'me-btn'}
        >
          <Save size={12} />
          {save.isPending ? 'Saving…' : 'Save client context'}
        </button>
        {isDirty && (
          <button
            onClick={() => {
              setFirmName(initial.firmName)
              setDescription(initial.description)
              setColleagues(initial.colleagues)
              setNotes(initial.notes)
              setExtras(initial.extras)
            }}
            className="me-btn"
            disabled={save.isPending}
          >
            Discard
          </button>
        )}
        {save.isSuccess && !isDirty && (
          <span className="me-mono text-[11px] text-[color:var(--me-fresh)]">saved</span>
        )}
        {save.error && (
          <span className="text-[12px] text-[color:var(--me-urgent)]">
            {(save.error as Error).message}
          </span>
        )}
      </div>

      <JsonPreview json={preview === '{}' ? '(empty)' : preview} />
    </div>
  )
}

// ─── Editable firm_lexicon panel ──────────────────────────────────────────
//
// Acronym → expansion dictionary, injected into prompts as
// {{firm_lexicon}}. Independent of client_context (a prompt does not
// receive both automatically; each is opt-in).
//
// Local state shape: ordered array of LexEntry rows. Each row has an
// abbreviation (key) + ≥1 reference (expansion). Stored on the server as
// a JSON object where single-reference rows collapse to a string and
// multi-reference rows stay as arrays. Empty references are kept in the
// UI during editing (so admins can type into a fresh row) and stripped
// on save by the server's validate_firm_lexicon. The frontend also
// strips on save to keep the JSON preview honest.

// Structured fields (signal/role/matterHint) are present only for object-form
// entries — recurring named entities that carry a matching signal, vs plain
// abbreviations. Emerson 2026-08-09.
type LexEntry = {
  abbreviation: string
  references: string[]
  signal?: string
  role?: string
  matterHint?: string
}
type LexValue = string | string[] | Record<string, unknown>

function lexiconObjToEntries(raw: Record<string, LexValue> | undefined): LexEntry[] {
  if (!raw) return []
  return Object.entries(raw).map(([k, v]) => {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const o = v as Record<string, unknown>
      const rt = o.resolves_to
      const refs = Array.isArray(rt) ? rt.map((s) => String(s))
        : rt != null ? [String(rt)] : ['']
      return {
        abbreviation: k,
        references: refs.length ? refs : [''],
        signal: o.signal ? String(o.signal) : undefined,
        role: o.role ? String(o.role) : undefined,
        matterHint: o.matter_hint ? String(o.matter_hint) : undefined,
      }
    }
    return { abbreviation: k, references: Array.isArray(v) ? v.map((s) => String(s)) : [String(v)] }
  })
}

function entriesToLexiconObj(entries: LexEntry[]): Record<string, LexValue> {
  const out: Record<string, LexValue> = {}
  for (const e of entries) {
    const key = e.abbreviation.trim()
    if (!key) continue
    const refs = e.references.map((s) => s.trim()).filter(Boolean)
    if (refs.length === 0) continue
    const signal = (e.signal || '').trim()
    const role = (e.role || '').trim()
    const hint = (e.matterHint || '').trim()
    if (signal || role || hint) {
      const obj: Record<string, unknown> = { resolves_to: refs.length === 1 ? refs[0] : refs }
      if (signal) obj.signal = signal
      if (role) obj.role = role
      if (hint) obj.matter_hint = hint
      out[key] = obj
    } else {
      out[key] = refs.length === 1 ? refs[0] : refs
    }
  }
  return out
}

function lexiconJsonStable(obj: Record<string, LexValue>): string {
  // Alphabetize keys so save-state comparison is order-insensitive.
  const sortedKeys = Object.keys(obj).sort()
  const sorted: Record<string, LexValue> = {}
  for (const k of sortedKeys) sorted[k] = obj[k]
  return Object.keys(sorted).length ? JSON.stringify(sorted, null, 2) : '{}'
}

function FirmLexiconPanel({ client }: { client: ClientSettings }) {
  const qc = useQueryClient()
  const [entries, setEntries] = useState<LexEntry[]>(
    () => lexiconObjToEntries(client.firm_lexicon)
  )

  useEffect(() => {
    setEntries(lexiconObjToEntries(client.firm_lexicon))
  }, [client.id, client.firm_lexicon])

  const built = useMemo(() => entriesToLexiconObj(entries), [entries])
  const preview = lexiconJsonStable(built)
  const previewServerSide = lexiconJsonStable(client.firm_lexicon || {})
  const isDirty = preview !== previewServerSide

  // Duplicate-key detection (after trim). Doesn't block save — server
  // would merge silently because object keys are unique — but warn so
  // the operator doesn't lose work to invisible collisions.
  const dupes = useMemo(() => {
    const seen = new Map<string, number>()
    const dup: string[] = []
    for (const e of entries) {
      const k = e.abbreviation.trim()
      if (!k) continue
      const n = (seen.get(k) || 0) + 1
      seen.set(k, n)
      if (n === 2) dup.push(k)
    }
    return dup
  }, [entries])

  const save = useMutation({
    mutationFn: () => clients.update(client.id, { firm_lexicon: built }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  })

  const updateAbbrev = (i: number, value: string) => {
    setEntries((prev) => prev.map((e, j) => j === i ? { ...e, abbreviation: value } : e))
  }
  const updateField = (i: number, field: 'signal' | 'role' | 'matterHint', value: string) => {
    setEntries((prev) => prev.map((e, j) => j === i ? { ...e, [field]: value } : e))
  }
  const updateRef = (i: number, ri: number, value: string) => {
    setEntries((prev) => prev.map((e, j) =>
      j === i ? { ...e, references: e.references.map((r, k) => k === ri ? value : r) } : e,
    ))
  }
  const removeRef = (i: number, ri: number) => {
    setEntries((prev) => prev.map((e, j) =>
      j === i
        // Removing the LAST reference would leave the entry refless; keep
        // one empty input so the entry stays editable. Use the row-level
        // trash to drop the whole entry instead.
        ? { ...e, references: e.references.length > 1 ? e.references.filter((_, k) => k !== ri) : [''] }
        : e,
    ))
  }
  const addRef = (i: number) => {
    setEntries((prev) => prev.map((e, j) =>
      j === i ? { ...e, references: [...e.references, ''] } : e,
    ))
  }
  const removeEntry = (i: number) => {
    setEntries((prev) => prev.filter((_, j) => j !== i))
  }
  const addEntry = () => {
    setEntries((prev) => [...prev, { abbreviation: '', references: [''] }])
  }

  return (
    <div className="mt-6 me-card p-5">
      <div className="me-section-h">Firm lexicon</div>
      <div className="text-[12px] me-text-mute mb-4 leading-snug">
        Acronym → expansion dictionary injected into prompts as{' '}
        <span className="me-mono">{'{{firm_lexicon}}'}</span>.{' '}
        Independent of <span className="me-mono">{'{{client_context}}'}</span> —
        a prompt receiving one does not automatically receive the other.
        Single-reference entries serialize as strings; multi-reference
        entries (e.g. <span className="me-mono">BDR</span>) serialize as
        arrays. Empty references are stripped on save.
      </div>

      <div className="flex items-center justify-between mb-2">
        <label className="text-[11.5px] font-semibold me-text">
          entries
          <span className="ml-1 me-text-mute font-normal">
            ({entries.length} {entries.length === 1 ? 'abbreviation' : 'abbreviations'})
          </span>
        </label>
        <button onClick={addEntry} className="me-btn me-btn-ghost text-[11px]">
          <Plus size={10} /> Add abbreviation
        </button>
      </div>
      {entries.length === 0 && (
        <div className="text-[11.5px] me-text-mute italic">
          No entries — click "Add abbreviation" to define one.
        </div>
      )}
      <div className="space-y-3">
        {entries.map((entry, i) => (
          <div key={i} className="grid grid-cols-[1fr_3fr_auto] gap-2 items-start">
            <input
              type="text"
              value={entry.abbreviation}
              onChange={(e) => updateAbbrev(i, e.target.value)}
              placeholder="e.g. BDR or Cordero"
              className="me-input me-mono text-[12px]"
            />
            <div className="space-y-1.5">
              {entry.references.map((ref, ri) => (
                <div key={ri} className="flex items-start gap-1.5">
                  <input
                    type="text"
                    value={ref}
                    onChange={(e) => updateRef(i, ri, e.target.value)}
                    placeholder='e.g. Borrower Defense to Repayment'
                    className="me-input text-[12px] flex-1"
                  />
                  <button
                    onClick={() => removeRef(i, ri)}
                    className="me-btn me-btn-ghost"
                    title={entry.references.length > 1
                      ? 'Remove this reference'
                      : 'Clear this reference (use the trash on the right to delete the whole entry)'}
                    aria-label="Remove reference"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                onClick={() => addRef(i)}
                className="me-btn me-btn-ghost text-[11px]"
              >
                <Plus size={9} /> add reference
              </button>
              {/* Optional matching-signal fields. Filling ANY of these promotes
                  the entry to an object ({resolves_to, signal, role, matter_hint})
                  and turns on L3 surfacing + the DECIDE boost — use for recurring
                  named parties (e.g. Cordero), not plain abbreviations. */}
              <div className="mt-1 grid grid-cols-[46px_1fr] items-center gap-x-2 gap-y-1 text-[11px] pl-1 border-l-2 me-border">
                <span className="me-text-mute">signal</span>
                <input type="text" value={entry.signal || ''}
                  onChange={(e) => updateField(i, 'signal', e.target.value)}
                  placeholder="distinctive phrase, e.g. campus police Title IX personnel investigation"
                  className="me-input text-[11.5px]" />
                <span className="me-text-mute">role</span>
                <input type="text" value={entry.role || ''}
                  onChange={(e) => updateField(i, 'role', e.target.value)}
                  placeholder="e.g. respondent"
                  className="me-input text-[11.5px]" />
                <span className="me-text-mute">matter</span>
                <input type="text" value={entry.matterHint || ''}
                  onChange={(e) => updateField(i, 'matterHint', e.target.value)}
                  placeholder="e.g. M-132"
                  className="me-input me-mono text-[11.5px]" />
              </div>
            </div>
            <button
              onClick={() => removeEntry(i)}
              className="me-btn me-btn-ghost"
              title="Delete this abbreviation and all of its references"
              aria-label="Remove abbreviation"
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
      </div>

      {dupes.length > 0 && (
        <div className="mt-3 text-[11.5px] text-[color:var(--me-urgent)]">
          ⚠ Duplicate abbreviation{dupes.length === 1 ? '' : 's'}:{' '}
          <span className="me-mono">{dupes.join(', ')}</span>. The
          last-row references for each abbreviation will win on save (object
          keys are unique).
        </div>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={() => save.mutate()}
          disabled={!isDirty || save.isPending}
          className={isDirty ? 'me-btn me-btn-primary' : 'me-btn'}
        >
          <Save size={12} />
          {save.isPending ? 'Saving…' : 'Save firm lexicon'}
        </button>
        {isDirty && (
          <button
            onClick={() => setEntries(lexiconObjToEntries(client.firm_lexicon))}
            className="me-btn"
            disabled={save.isPending}
          >
            Discard
          </button>
        )}
        {save.isSuccess && !isDirty && (
          <span className="me-mono text-[11px] text-[color:var(--me-fresh)]">saved</span>
        )}
        {save.error && (
          <span className="text-[12px] text-[color:var(--me-urgent)]">
            {(save.error as Error).message}
          </span>
        )}
      </div>

      <JsonPreview json={preview === '{}' ? '(empty)' : preview} />
    </div>
  )
}


// Same Copy/Copied affordance the artifact viewer uses (BodyPreview
// header). 1.5s feedback window then back to "Copy".
function JsonPreview({ json }: { json: string }) {
  const [copied, setCopied] = useState(false)
  const doCopy = () => {
    if (!navigator.clipboard) return
    navigator.clipboard.writeText(json).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }).catch(() => { /* clipboard denied — silent */ })
  }
  return (
    <div className="mt-5">
      <div className="mb-1 flex items-center justify-between">
        <div className="text-[11px] font-semibold me-text-mute uppercase tracking-wider">
          Live preview — JSON the AI will see
        </div>
        <button
          onClick={doCopy}
          className="me-btn me-btn-outline"
          title="Copy the JSON to clipboard"
        >
          {copied ? <Check size={11} /> : <Copy size={11} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="me-mono text-[11px] me-text whitespace-pre-wrap break-words p-3 rounded border me-border bg-[color:var(--me-surface)] max-h-96 overflow-auto">
        {json}
      </pre>
    </div>
  )
}


// ─── Editable generic_terms panel ────────────────────────────────────────
//
// Ken 2026-07-19 — the deterministic Fan-Out entity-gate (see
// apps/pipeline/anti_echo.py) reads this dict. Any string in any
// list-valued key contributes to the generic pool. Free-shape keys
// (roles / doc_types / custom / ...) so Ken can group terms however he
// finds useful without a schema migration.

type GenericGroup = { key: string; terms: string[] }

function _termsToGroups(obj: Record<string, string[]> | undefined): GenericGroup[] {
  if (!obj || typeof obj !== 'object') return []
  const groups: GenericGroup[] = []
  for (const [key, val] of Object.entries(obj)) {
    if (Array.isArray(val)) {
      groups.push({ key, terms: val.filter((s) => typeof s === 'string') })
    }
  }
  return groups
}

function _groupsToObj(groups: GenericGroup[]): Record<string, string[]> {
  const out: Record<string, string[]> = {}
  for (const g of groups) {
    const key = g.key.trim()
    if (!key) continue
    const terms = g.terms.map((s) => s.trim()).filter((s) => s.length > 0)
    if (terms.length) out[key] = terms
  }
  return out
}

function GenericTermsPanel({ client }: { client: ClientSettings }) {
  const qc = useQueryClient()
  const [groups, setGroups] = useState<GenericGroup[]>(
    () => _termsToGroups(client.generic_terms),
  )
  useEffect(() => {
    setGroups(_termsToGroups(client.generic_terms))
  }, [client.id, client.generic_terms])

  const built = useMemo(() => _groupsToObj(groups), [groups])
  const dirty = JSON.stringify(built) !== JSON.stringify(client.generic_terms || {})

  const save = useMutation({
    mutationFn: () => clients.update(client.id, { generic_terms: built } as Partial<ClientSettings>),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['aiConfigClient'] })
      qc.invalidateQueries({ queryKey: ['currentClient'] })
    },
  })

  function addGroup() {
    setGroups((prev) => [...prev, { key: '', terms: [] }])
  }
  function removeGroup(i: number) {
    setGroups((prev) => prev.filter((_, ix) => ix !== i))
  }
  function setGroupKey(i: number, key: string) {
    setGroups((prev) => prev.map((g, ix) => (ix === i ? { ...g, key } : g)))
  }
  function setGroupTermsText(i: number, text: string) {
    // Each line is one term. Blank lines are dropped on save (see _groupsToObj).
    const terms = text.split('\n').map((s) => s.replace(/\r$/, ''))
    setGroups((prev) => prev.map((g, ix) => (ix === i ? { ...g, terms } : g)))
  }

  return (
    <div className="me-card mt-3 p-4">
      <div className="mb-2 flex items-center justify-between">
        <div>
          <div className="me-section-h">generic_terms</div>
          <div className="text-[11px] me-text-mute">
            Deterministic Fan-Out gate reads this list. Any entity in the
            LLM's <span className="me-mono">entities_found_in_matter</span>{' '}
            that is substantially one of these terms counts as
            <span className="ml-1 font-medium">generic</span>. When every
            listed entity is generic (or the list is empty), the assessment
            is forced to <span className="me-mono">does_belong=false</span>.
            No LLM cost — pure membership. Add/edit here; no code
            change to update.
            <br />
            <span className="me-text-dim">
              Note: <span className="me-mono">client_context</span> is <em>not</em> consulted by the gate.
              Colleague names are specific-enough context to anchor a real match.
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={addGroup}
            className="me-btn me-btn-outline !text-[11px] !px-2 !py-1"
          >
            + group
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
            className="me-btn me-btn-primary !text-[11px] !px-2 !py-1 disabled:opacity-40"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      {groups.length === 0 && (
        <div className="text-[11.5px] me-text-mute italic">
          No generic groups yet. Add one (e.g. <span className="me-mono">roles</span>,{' '}
          <span className="me-mono">doc_types</span>, <span className="me-mono">custom</span>)
          and put one term per line.
        </div>
      )}
      <div className="mt-2 grid gap-2 md:grid-cols-2">
        {groups.map((g, i) => (
          <div key={i} className="me-card p-2">
            <div className="mb-1 flex items-center gap-2">
              <input
                value={g.key}
                onChange={(e) => setGroupKey(i, e.target.value)}
                placeholder="group key (e.g. roles)"
                className="me-input !text-[12px] flex-1"
              />
              <button
                onClick={() => removeGroup(i)}
                className="me-btn me-btn-outline !text-[10px] !px-1.5 !py-0.5"
                title="Remove this group"
              >
                remove
              </button>
            </div>
            <textarea
              value={g.terms.join('\n')}
              onChange={(e) => setGroupTermsText(i, e.target.value)}
              placeholder="one term per line"
              className="me-input !text-[11.5px] font-mono w-full"
              style={{ minHeight: 140 }}
            />
            <div className="mt-1 text-[10.5px] me-text-mute">
              {g.terms.filter((s) => s.trim()).length} term(s)
            </div>
          </div>
        ))}
      </div>
      {save.isError && (
        <div className="mt-2 text-[11px] text-[color:var(--me-urgent)]">
          {(save.error as Error).message}
        </div>
      )}
    </div>
  )
}


// ─── Pre-seed matters (Ken 2026-07-19 dev ticket) ─────────────────
// Materialize known long-lived matters BEFORE ingest — the very
// point is to make L4 fanout consider them on the next email, so
// the AI files into them instead of AI-discovering a duplicate then
// requiring a refile.
//
// Notes on wiring (Ken's ticket flagged these; verified before build):
//   - L4 enumerates by matter.status ∈ {proposed, intake, active,
//     on_hold} — NOT filtered by artifact_count. Zero-artifact
//     pre-seeds are visible.
//   - keywords[] is packed into matter.description on save (Ken's
//     "start simple" approach) so the existing fan-out prompt
//     picks them up without a prompt change.
//   - When the first real email lands + L6 rewrites description,
//     the packed keywords get overwritten — that's fine, by then
//     the matter has real state.

const MATTER_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'litigation', label: 'Litigation' },
  { value: 'contract', label: 'Contract' },
  { value: 'employment', label: 'Employment' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'ada_compliance', label: 'ADA Compliance' },
  { value: 'civil_rights', label: 'Civil Rights' },
  { value: 'policy', label: 'Policy' },
  { value: 'public_records_request', label: 'Public Records Request' },
  { value: 'student_affairs', label: 'Student Affairs' },
  { value: 'cabinet_institutional', label: 'Cabinet / Institutional' },
  { value: 'operations', label: 'Operations' },
  { value: 'forms', label: 'Forms' },
  { value: 'other', label: 'Other' },
]

type PreseedDraft = {
  matter_name: string
  matter_type: string
  folder_id: number | null
  keywords_text: string  // one keyword per line — parsed on save
}

function _emptyDraft(): PreseedDraft {
  return { matter_name: '', matter_type: 'litigation', folder_id: null, keywords_text: '' }
}

function _keywordsFromText(text: string): string[] {
  return text.split('\n').map((s) => s.trim()).filter((s) => s.length > 0)
}

function _preseedToDraft(p: PreseedMatter): PreseedDraft {
  // Ken 2026-07-20 fix — populate the edit form from the AUTHORITATIVE
  // `keywords` array, not from parsing description prose. Was pulling
  // from description via regex, but description isn't re-packed once
  // the matter has real artifacts, so users would see their newly-
  // added keywords "disappear" on next open even though the DB kept
  // them fine. Falls back to the description-regex parse only for the
  // rare case where a legacy client happened to send a preseed row
  // without a keywords[] field.
  let kws: string[] = []
  if (Array.isArray(p.keywords) && p.keywords.length > 0) {
    kws = p.keywords.filter((s): s is string => typeof s === 'string' && s.length > 0)
  } else {
    const m = /^Keywords:\s*(.+)$/im.exec(p.description || '')
    if (m) kws = m[1].split(';').map((s) => s.trim()).filter(Boolean)
  }
  return {
    matter_name: p.matter_name,
    matter_type: p.matter_type || 'other',
    folder_id: p.folder_id,
    keywords_text: kws.join('\n'),
  }
}

// Emerson 2026-07-19 focus-loss fix — DraftRow was defined inside
// PreseedMattersPanel, which meant every parent render created a
// brand-new component identity. React unmounted the whole input tree
// on each keystroke → cursor left the box after every character.
// Hoisted to a module-level function; folderOptions comes in as a prop
// now instead of closing over parent state.
function PreseedDraftRow({
  draft, onChange, onSubmit, submitting, submitLabel, folderOptions,
  keywordsLocked = false,
}: {
  draft: PreseedDraft
  onChange: (d: PreseedDraft) => void
  onSubmit: () => void
  submitting: boolean
  submitLabel: string
  folderOptions: Folder[]
  // Once a pre-seed has accumulated real artifacts it's a live matter:
  // L5/L6 regenerate matter.keywords from its filed documents on every
  // run, and L4 fan-out routes on matter.description (not this array), so
  // a keyword edit here is silently overwritten and changes nothing about
  // filing. Lock the textarea and say so, rather than accepting a save
  // that reports success but is inert. name / type / folder stay editable
  // — those DO persist for a live matter.
  keywordsLocked?: boolean
}) {
  const disabled = submitting || !draft.matter_name.trim()
  return (
    <div className="me-card p-2">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-[1.5fr_1fr_1fr]">
        <input
          value={draft.matter_name}
          onChange={(e) => onChange({ ...draft, matter_name: e.target.value })}
          placeholder="matter_name (e.g. Blockson)"
          className="me-input !text-[12px]"
        />
        <select
          value={draft.matter_type}
          onChange={(e) => onChange({ ...draft, matter_type: e.target.value })}
          className="me-input !text-[12px]"
        >
          {MATTER_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={draft.folder_id ?? ''}
          onChange={(e) => onChange({
            ...draft,
            folder_id: e.target.value ? Number(e.target.value) : null,
          })}
          className="me-input !text-[12px]"
        >
          <option value="">(no folder)</option>
          {folderOptions.map((f: Folder) => (
            <option key={f.id} value={f.id}>{f.name}</option>
          ))}
        </select>
      </div>
      {keywordsLocked ? (
        <div className="mt-2 rounded border me-border bg-[color:var(--me-surface)] p-2">
          <div className="text-[10.5px] me-text-mute leading-snug">
            This pre-seed has filed artifacts, so it's now a live matter.
            Keywords are regenerated from its documents on every pipeline
            run, and mail is routed on the matter's description — not this
            list — so editing keywords here is overwritten and does not
            change routing. To change what belongs here, Re-file the
            matter's documents through the normal matter workflow.
          </div>
          {_keywordsFromText(draft.keywords_text).length > 0 && (
            <details className="mt-1.5">
              <summary className="cursor-pointer text-[10.5px] me-text-mute">
                current keywords ({_keywordsFromText(draft.keywords_text).length})
              </summary>
              <pre className="mt-1 whitespace-pre-wrap text-[10.5px] me-text-mute font-mono">
                {draft.keywords_text}
              </pre>
            </details>
          )}
        </div>
      ) : (
        <textarea
          value={draft.keywords_text}
          onChange={(e) => onChange({ ...draft, keywords_text: e.target.value })}
          placeholder="one keyword per line — e.g.&#10;Blockson v. Moravian&#10;Laquita Blockson&#10;EEOC Charge No. 530-2021-00746"
          className="me-input !text-[11.5px] font-mono w-full mt-2"
          style={{ minHeight: 120 }}
        />
      )}
      <div className="mt-1 flex items-center justify-between">
        <span className="text-[10.5px] me-text-mute">
          {keywordsLocked
            ? 'keywords locked — live matter'
            : `${_keywordsFromText(draft.keywords_text).length} keyword(s)`}
        </span>
        <button
          onClick={onSubmit}
          disabled={disabled}
          className="me-btn me-btn-primary !text-[11px] !px-2 !py-1 disabled:opacity-40"
        >
          {submitting ? 'Saving…' : submitLabel}
        </button>
      </div>
    </div>
  )
}


function PreseedMattersPanel() {
  const qc = useQueryClient()
  const preseeds = useQuery({
    queryKey: ['preseedMatters'],
    queryFn: () => preseedMatters.list(),
  })
  const folders = useQuery({
    queryKey: ['foldersTopLevel'],
    // /api/folders/ actually returns DRF paginated `{results: [...]}`
    // shape even though the api.ts type says `Folder[]` — normalize
    // here so the useMemo below can safely call .filter(). Was the
    // flicker-then-blank AI Config crash: raw.filter is not a function.
    queryFn: async () => {
      const raw = (await foldersApi.list()) as unknown
      if (Array.isArray(raw)) return raw as Folder[]
      const results = (raw as { results?: Folder[] } | null)?.results
      return Array.isArray(results) ? results : []
    },
  })

  const [newDraft, setNewDraft] = useState<PreseedDraft>(_emptyDraft())
  const [editing, setEditing] = useState<Record<number, PreseedDraft>>({})
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null)

  const create = useMutation({
    mutationFn: (draft: PreseedDraft) => preseedMatters.create({
      matter_name: draft.matter_name,
      matter_type: draft.matter_type,
      folder_id: draft.folder_id,
      keywords: _keywordsFromText(draft.keywords_text),
    }),
    onSuccess: () => {
      setNewDraft(_emptyDraft())
      qc.invalidateQueries({ queryKey: ['preseedMatters'] })
    },
  })

  const update = useMutation({
    mutationFn: ({ id, draft }: { id: number; draft: PreseedDraft }) =>
      preseedMatters.update(id, {
        matter_name: draft.matter_name,
        matter_type: draft.matter_type,
        folder_id: draft.folder_id,
        keywords: _keywordsFromText(draft.keywords_text),
      }),
    onSuccess: (_, { id }) => {
      setEditing((prev) => {
        const next = { ...prev }
        delete next[id]
        return next
      })
      qc.invalidateQueries({ queryKey: ['preseedMatters'] })
    },
  })

  const remove = useMutation({
    mutationFn: (id: number) => preseedMatters.remove(id),
    onSuccess: () => {
      setConfirmDelete(null)
      qc.invalidateQueries({ queryKey: ['preseedMatters'] })
    },
  })

  const folderOptions = useMemo(() => {
    const raw = folders.data ?? []
    return raw
      .filter((f: Folder) => !f.parent_folder)
      .sort((a: Folder, b: Folder) => a.name.localeCompare(b.name))
  }, [folders.data])

  const rows = preseeds.data?.preseeds ?? []

  return (
    <div className="me-card mt-3 p-4">
      <div className="mb-2">
        <div className="me-section-h">Pre-seed matters</div>
        <div className="text-[11px] me-text-mute">
          Materialize known long-lived matters BEFORE ingest so mail files
          into them on the next run (instead of being AI-discovered then
          refiled). Each row becomes a real{' '}
          <span className="me-mono">Matter</span> with{' '}
          <span className="me-mono">status='active'</span> and{' '}
          <span className="me-mono">is_preseed=true</span>. Keywords pack
          into <span className="me-mono">matter.description</span> so the
          fan-out prompt reads them without a prompt change.
          <br />
          <span className="me-text-dim">
            Super-user only. Verified: L4 fanout enumerates by status,
            not by artifact-count, so zero-artifact pre-seeds show up
            immediately.
          </span>
        </div>
      </div>

      {/* Existing pre-seeds */}
      <div className="mt-3 space-y-2">
        {rows.length === 0 && !preseeds.isPending && (
          <div className="text-[11.5px] me-text-mute italic">
            No pre-seeds yet. Add one below.
          </div>
        )}
        {rows.map((p: PreseedMatter) => {
          const isEditing = editing[p.matter_id] !== undefined
          const draft = editing[p.matter_id]
          return (
            <div key={p.matter_id} className="me-card p-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="me-mono text-[10.5px] me-text-mute">M-{String(p.client_seq).padStart(3, '0')}</span>
                  <span className="text-[12px] font-medium me-text">{p.matter_name}</span>
                  <span className="me-chip text-[9.5px]">{p.matter_type}</span>
                  {p.folder_name && (
                    <span className="me-chip text-[9.5px]">{p.folder_name}</span>
                  )}
                  {p.artifact_count > 0 && (
                    <span className="me-chip me-chip-stale text-[9.5px]" title="Real artifacts have landed — editing description is disabled to protect L6's authored version">
                      {p.artifact_count} artifact(s)
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {!isEditing && (
                    <button
                      onClick={() => setEditing((prev) => ({ ...prev, [p.matter_id]: _preseedToDraft(p) }))}
                      className="me-btn me-btn-outline !text-[10px] !px-2 !py-0.5"
                    >
                      Edit
                    </button>
                  )}
                  {isEditing && (
                    <button
                      onClick={() => setEditing((prev) => {
                        const next = { ...prev }
                        delete next[p.matter_id]
                        return next
                      })}
                      className="me-btn me-btn-outline !text-[10px] !px-2 !py-0.5"
                    >
                      Cancel
                    </button>
                  )}
                  {confirmDelete === p.matter_id ? (
                    <>
                      <button
                        onClick={() => remove.mutate(p.matter_id)}
                        disabled={remove.isPending}
                        className="me-btn !text-[10px] !px-2 !py-0.5 bg-[color:var(--me-urgent)] text-white"
                      >
                        {remove.isPending ? '…' : 'Confirm delete'}
                      </button>
                      <button
                        onClick={() => setConfirmDelete(null)}
                        className="me-btn me-btn-outline !text-[10px] !px-2 !py-0.5"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setConfirmDelete(p.matter_id)}
                      className="me-btn me-btn-outline !text-[10px] !px-2 !py-0.5"
                      title={p.artifact_count > 0
                        ? 'This preseed has artifacts and can no longer be deleted here — close through normal matter workflow'
                        : 'Delete this preseed (only allowed while artifact_count=0)'}
                      disabled={p.artifact_count > 0}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
              {isEditing && (
                <div className="mt-2">
                  <PreseedDraftRow
                    draft={draft}
                    onChange={(d) => setEditing((prev) => ({ ...prev, [p.matter_id]: d }))}
                    onSubmit={() => update.mutate({ id: p.matter_id, draft })}
                    submitting={update.isPending}
                    submitLabel="Save changes"
                    folderOptions={folderOptions}
                    keywordsLocked={p.artifact_count > 0}
                  />
                </div>
              )}
              {!isEditing && p.description && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-[10.5px] me-text-mute">description</summary>
                  <pre className="mt-1 whitespace-pre-wrap text-[10.5px] me-text-mute font-mono">{p.description}</pre>
                </details>
              )}
            </div>
          )
        })}
      </div>

      {/* Add-new */}
      <div className="mt-3">
        <div className="me-section-h mb-1">Add pre-seed</div>
        <PreseedDraftRow
          draft={newDraft}
          onChange={setNewDraft}
          onSubmit={() => create.mutate(newDraft)}
          submitting={create.isPending}
          submitLabel="Create pre-seed"
          folderOptions={folderOptions}
        />
      </div>

      {create.isError && (
        <div className="mt-2 text-[11px] text-[color:var(--me-urgent)]">
          {(create.error as any)?.response?.data?.detail || (create.error as Error).message}
        </div>
      )}
      {update.isError && (
        <div className="mt-2 text-[11px] text-[color:var(--me-urgent)]">
          {(update.error as any)?.response?.data?.detail || (update.error as Error).message}
        </div>
      )}
      {remove.isError && (
        <div className="mt-2 text-[11px] text-[color:var(--me-urgent)]">
          {(remove.error as any)?.response?.data?.detail || (remove.error as Error).message}
        </div>
      )}
    </div>
  )
}
